import React, { useContext, useEffect, useRef, useState } from 'react';
import Recaptcha from 'react-google-recaptcha';
import { z } from 'zod';
import { Link } from 'gatsby';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { HiOutlinePhone, HiOutlineUser } from 'react-icons/hi';
import { MdOutlineEmail } from 'react-icons/md';

import { useUiComponentsInternationalContext } from '@repo/static-translations/src';
import { SiteSettingsContext } from '@repo/contexts/SiteSettingsContext';
import { Button } from '@repo/ui-components/shadcn/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui-components/shadcn/Form';
import { Input } from '@repo/ui-components/shadcn/Input';
import { pushToDataLayer } from './utils';
import {
  PersonalDetailsFormLabels,
  personalDetailsFormSchema,
} from './formSchema';
import { PersonalDetailsProps } from './BlockBuilderProspectScheduler.types';
import AddressField from './AddressField';

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  streetAddress,
  postalCode,
  referredByCode,
  handlePersonalDetailsChange,
  handlePersonalInfoSubmit,
  loading,
  personalSubmitButtonLabel,
}) => {
  const recaptchaRef = useRef<Recaptcha>(null);
  const formFillRef = useRef(false); // Tracks if message has been logged
  const [recaptchaError, setRecaptchaError] = useState(false);

  const { tt } = useUiComponentsInternationalContext();

  const { siteSettings } = useContext(SiteSettingsContext);

  const formLabels = {
    firstName: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .firstNameRequiredValidationMessage
    ),
    lastName: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .lastNameRequiredValidationMessage
    ),
    emailRequired: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent.emailRequiredValidationMessage
    ),
    emailValid: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .useValidEmailAddressValidationMessage
    ),
    phoneNumber: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .useValidPhoneNumberValidationMessage
    ),
    streetAddress: tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .addressRequiredValidationMessage
    ),
  } as PersonalDetailsFormLabels;

  const schemaPersonalDetails = personalDetailsFormSchema(formLabels);
  type FormInputs = z.infer<typeof schemaPersonalDetails>;

  const form = useForm<FormInputs>({
    resolver: zodResolver(personalDetailsFormSchema(formLabels)),
    mode: 'all',
    defaultValues: {
      firstName,
      lastName,
      email,
      phoneNumber,
      streetAddress,
      postalCode,
      referredByCode,
    },
  });

  const { handleSubmit, watch } = form;

  // try and get the referral code from the URL
  useEffect(() => {
    const referralCode = sessionStorage.getItem('referral_code');

    if (referralCode) {
      form.setValue('referredByCode', referralCode);
      handlePersonalDetailsChange('referredByCode', referralCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // data layer event for initial form fill
  useEffect(() => {
    const subscription = watch(values => {
      if (!formFillRef.current) {
        const hasStartedFilling = Object.values(values).some(value => !!value);
        if (hasStartedFilling) {
          pushToDataLayer({ event: 'prospect_form_begin_fill' });
          formFillRef.current = true;
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (!loading) {
      recaptchaRef.current?.reset();
    }
  }, [loading]);

  const onSubmit: SubmitHandler<FormInputs> = async () => {
    if (!recaptchaRef.current) {
      return;
    }

    try {
      const token = await recaptchaRef.current.executeAsync();
      if (token) {
        handlePersonalInfoSubmit(token);
      } else {
        // This case might occur if executeAsync for some reason resolves to null/undefined
        // without throwing an error.
        console.error('reCAPTCHA token was not received.');
        setRecaptchaError(true);
      }
    } catch (error) {
      console.error('An error occurred during reCAPTCHA execution:', error);
      setRecaptchaError(true);
    }
  };

  return (
    <Form {...form}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <Recaptcha
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.GATSBY_RECAPTCHA_KEY_INVISIBLE}
          onErrored={() => {
            console.error('reCAPTCHA failed to load.');
            setRecaptchaError(true);
          }}
        />

        <FormDescription>
          {tt(
            s =>
              s.blockBuilderReferralFormComponent
                .unlessSpecifiedAllFieldsRequiredHint
          )}
        </FormDescription>
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {tt(
                  s => s.blockBuilderProspectSchedulerComponent.firstNameLabel
                )}
              </FormLabel>
              <FormControl>
                <Input
                  iconComponent={<HiOutlineUser size="22" />}
                  hasError={!!form.formState.errors.firstName}
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    handlePersonalDetailsChange(e.target.name, e.target.value);
                  }}
                  required
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {tt(
                  s => s.blockBuilderProspectSchedulerComponent.lastNameLabel
                )}
              </FormLabel>
              <FormControl>
                <Input
                  iconComponent={<HiOutlineUser size="22" />}
                  hasError={!!form.formState.errors.lastName}
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    handlePersonalDetailsChange(e.target.name, e.target.value);
                  }}
                  required
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent.emailAddressLabel
                )}
              </FormLabel>
              <FormControl>
                <Input
                  iconComponent={<MdOutlineEmail size="22" />}
                  hasError={!!form.formState.errors.email}
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    handlePersonalDetailsChange(e.target.name, e.target.value);
                  }}
                  inputMode="email"
                  required
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {tt(
                  s => s.blockBuilderProspectSchedulerComponent.phoneNumberLabel
                )}
              </FormLabel>
              <FormControl>
                <Input
                  iconComponent={<HiOutlinePhone size="22" />}
                  hasError={!!form.formState.errors.phoneNumber}
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    handlePersonalDetailsChange(e.target.name, e.target.value);
                  }}
                  inputMode="numeric"
                  required
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Address fields extracted into their own component */}
        <AddressField
          form={form}
          handlePersonalDetailsChange={handlePersonalDetailsChange}
        />

        <FormField
          control={form.control}
          name="referredByCode"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent.referralCodeLabel
                )}
              </FormLabel>
              <FormDescription>
                {tt(
                  s => s.blockBuilderProspectSchedulerComponent.optionalLabel
                )}
              </FormDescription>
              <FormControl>
                <Input
                  hasError={!!form.formState.errors.referredByCode}
                  {...field}
                  onChange={e => {
                    field.onChange(e);
                    handlePersonalDetailsChange(e.target.name, e.target.value);
                  }}
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="pt-2">
          <Button
            className="mb-4"
            variant="primary"
            type="submit"
            disabled={loading ?? recaptchaError}
          >
            {loading
              ? tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent.buttonLoadingText
                )
              : personalSubmitButtonLabel ??
                tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent
                      .buttonPersonalSubmitText
                )}
          </Button>

          {recaptchaError && (
            <p className="text-red-500 text-sm mt-2">
              We couldn&apos;t load the security check (reCAPTCHA). Please try
              again later or contact us if the issue persists.
            </p>
          )}
        </div>
      </form>
      <div className="text-sm py-3">
        {tt(s => s.blockBuilderProspectSchedulerComponent.consentText, {
          privacyPolicyLink: (
            <Link to="/privacy-policy">
              {tt(
                s => s.blockBuilderPrivacyPolicyComponent.privacyPolicyHeader
              )}
            </Link>
          ),
          phoneLink: (
            <a href={`tel:${siteSettings.phone}`}>{siteSettings.phone}</a>
          ),
        })}
      </div>
    </Form>
  );
};

export default PersonalDetails;
